import { useEffect, useRef, useState } from 'react';

export const useHeldKeyboardKey = (key: string, delay = 750): boolean => {
    const [held, setHeld] = useState(false);
    const pressedRef = useRef(false);
    const cancelledRef = useRef(false);
    const timeoutRef = useRef<number>();

    useEffect(() => {
        const clearHoldTimeout = () => {
            if (timeoutRef.current === undefined) {
                return;
            }

            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        };

        const resetHold = () => {
            clearHoldTimeout();
            pressedRef.current = false;
            cancelledRef.current = false;
            setHeld(false);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === key) {
                if (event.repeat || pressedRef.current) {
                    return;
                }

                pressedRef.current = true;
                cancelledRef.current = event.altKey || event.ctrlKey || event.metaKey;

                if (cancelledRef.current) {
                    return;
                }

                timeoutRef.current = window.setTimeout(() => {
                    timeoutRef.current = undefined;

                    if (pressedRef.current && !cancelledRef.current) {
                        setHeld(true);
                    }
                }, delay);

                return;
            }

            if (!pressedRef.current || cancelledRef.current) {
                return;
            }

            cancelledRef.current = true;
            clearHoldTimeout();
            setHeld(false);
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            if (event.key === key) {
                resetHold();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                resetHold();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        window.addEventListener('keyup', handleKeyUp, true);
        window.addEventListener('blur', resetHold);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearHoldTimeout();
            pressedRef.current = false;
            cancelledRef.current = false;
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('keyup', handleKeyUp, true);
            window.removeEventListener('blur', resetHold);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [delay, key]);

    return held;
};
